import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { createComment } from '@/modules/Ticket/services'
import { TicketCommentPayload } from '@/modules/Ticket/types/payload'
import { IPermissionProps } from '@/types/shared'
import { useTranslation } from 'react-i18next'
import { Button, FormItem } from 'vnetwork-ui'
import useAuthStore from '@/stores/useAuthStore'
import TicketCommentsList from './List'
import useDataGridRefetch from '@/hooks/useDataGridRefetch'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

const TicketProcessing: React.FC<IPermissionProps> = () => {
    const { t } = useTranslation()
    const { employee } = useAuthStore()
    const { ticketID } = useParams<{ ticketID: string }>()
    const { refetchCurrent } = useDataGridRefetch({
        reactQueryKey: 'ticket-comment',
    })
    const { refetchAll: refetchLogs } = useDataGridRefetch({
        reactQueryKey: `ticket-logs-${ticketID}`,
    })
    // Default off = internal staff note. Toggle on to send the reply to the
    // customer (becomes a 'public' comment in the partner portal).
    const [sendToCustomer, setSendToCustomer] = useState(false)
    const { control, handleSubmit, reset } = useForm<TicketCommentPayload>({
        defaultValues: {
            ticket_id: Number(ticketID) || 0,
            ticket_comment_text: '',
            ticket_comment_pinted: '',
            employee_id: employee?.employee_id || -1,
        },
    })

    const { mutateAsync: createCommentMutation, isPending } =
        useMutationWithHandler(
            { mutationFn: createComment },
            {
                reactQueryKeys: QUERY_KEYS.TICKETS,
                successMessage: t('create-success'),
                onSuccess: () => {
                    refetchCurrent()
                    refetchLogs()
                },
            }
        )

    const onSubmit = async (data: TicketCommentPayload) => {
        const payload = {
            ...data,
            ticket_id: Number(ticketID) || 0,
            employee_id: employee?.employee_id || -1,
            ticket_comment_pinted: 'f',
            ticket_comment_visibility: sendToCustomer ? 'public' : 'internal',
        }
        try {
            await createCommentMutation(payload)
            reset({ ticket_comment_text: '' })
        } catch (error) {
            console.error('Error submitting form', error)
        }
    }

    return (
        <div className="mt-2 bg-white rounded-lg p-4">
            <div className="my-4">
                <TicketCommentsList refetchCurrent={refetchCurrent} />
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <FormItem
                    control={control}
                    name="ticket_comment_text"
                    type="textarea"
                    placeholder={t('ticket-comment-text')}
                />
                <div className="flex items-center justify-between gap-3">
                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={sendToCustomer}
                            onChange={(e) =>
                                setSendToCustomer(e.target.checked)
                            }
                            disabled={isPending}
                        />
                        <span>
                            Gửi phản hồi cho khách hàng
                            <span className="text-gray-400">
                                {' '}
                                — khách sẽ thấy ở Partner Portal (mặc định: ghi
                                chú nội bộ)
                            </span>
                        </span>
                    </label>
                    <Button
                        size="small"
                        variant="contained"
                        type="submit"
                        loading={isPending}
                        disabled={isPending}
                        onClick={handleSubmit(onSubmit)}
                    >
                        Gửi
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default TicketProcessing
